/* A thin embedded list for the counterparty card's document tabs — Orders, Returns, Payments and
   Debts: one component, given the endpoint and its columns. It is NOT `orders/List.js` / `returns/List.js`: those are
   full-screen pages whose filters and paging live in the URL, so two of them on one page would
   fight for the query string, and their `buildQuery` is deliberately private. Here paging is local
   tab state and the only fixed filter is `?counterparty=<id>`.

   `excalibur/orders/` and `excalibur/returns/` are DRF-paginated, so the response is `{ results,
   count }`, read through `call` + `isOk` (a failed request hands back a TRUTHY `failedResponse()`),
   and only a body with `results` counts. A clickable row calls `onRowClick` (the Orders and Returns
   tabs open their record modal). The card mounts one section at a time (its own `cp-rail`, not antd
   `<Tabs>`), so this mounts — and fetches — only when its section is opened.

   Props beside `endpoint` / `columns`:
     `detailPath`  names the footer's ids (`cp-docs-…`); without it, the endpoint does;
     `query`       extra request params beside `counterparty` / paging (the window's `date_from`);
     `onRowClick`  what a row click does;
     `clickableRows` false: a row opens nothing — no click handler, no pointer cursor (the Payments
                   and Debts tabs: neither record has a page or modal of its own);
     `renderSummary` gets the response's own `summary` (the Payments tab's count and sum over the
                   whole window, not the page) and draws it above the table, under the section heading —
                   only when the window has rows and the load did not fail;
     `highlightId` the id of a row to point at (the Debts tab, opened from a payment's debt): once
                   it has loaded on the current page it is scrolled into view and washed for
                   `FLASH_MS` — a timer, not an animation, so it still shows under reduced motion.
                   A row that is not on the page is simply not marked.
   The table is content-sized — `scroll.x: "max-content"` with `/sales/orders`' `lp-fit` layout
   (`list-page.scss`) — and scrolls inside the card below that width.
   The FIRST load shows a skeleton, so an empty table does not flash its "nothing here" before the
   rows arrive; a page change after that keeps the table's own spinner, so the rows do not jump.
   Columns, their widths and alignment are the caller's (the Orders tab mirrors `/sales/orders`).
   The card-edge insets are `.cp-docstab` (`counterparties.scss`). */

import React, { useEffect, useRef, useState } from "react";
import { Empty, Flex, Skeleton, Table } from "antd";

import { TableFoot } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE } from "../../config/lang";
import { call, isOk } from "../../util/action";
import { translate } from "../../util/util";

import { LoadError } from "./tabParts";

const PAGE_SIZES = [10, 25, 50];

const NO_QUERY = {};

const FLASH_MS = 2000;

const DocsTab = ({
  counterpartyId,
  endpoint,
  detailPath,
  columns,
  emptyText,
  lang,
  query = NO_QUERY,
  onRowClick,
  clickableRows = true,
  renderSummary,
  highlightId,
}) => {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [flashId, setFlashId] = useState(null);
  const flashedFor = useRef(null);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(PAGE_SIZES[1]);
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  /* Compared by content, so a caller building `query` inline on every render does not refetch. */
  const queryKey = JSON.stringify(query);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ counterparty: counterpartyId, ...JSON.parse(queryKey), page, page_size: size });
    /* `call()` cannot be aborted: a page / size change while a request is in flight must not let
       the older answer land last. */
    let stale = false;
    call({ method: "GET", url: `${endpoint}?${params}` }, response => {
      if (stale) return;
      setLoadedOnce(true);
      if (!isOk(response) || !response.results) {
        setRows([]);
        setCount(0);
        setSummary(null);
        setError(true);
        setLoading(false);
        return;
      }
      setRows(response.results);
      setCount(response.count || 0);
      setSummary(response.summary || null);
      setError(false);
      setLoading(false);
    });
    return () => {
      stale = true;
    };
  }, [counterpartyId, endpoint, queryKey, page, size, reloadKey]);

  /* Once per `highlightId`: the first load that has its row marks it, then the mark goes. */
  useEffect(() => {
    if (!highlightId || loading || flashedFor.current === highlightId) return;
    if (!rows.some(row => String(row.id) === String(highlightId))) return;
    flashedFor.current = highlightId;
    setFlashId(String(highlightId));
  }, [highlightId, loading, rows]);

  useEffect(() => {
    if (!flashId) return undefined;
    document.querySelector(`.cp-docstab tr[data-row-key="${CSS.escape(flashId)}"]`)?.scrollIntoView({ block: "center" });
    const timer = setTimeout(() => setFlashId(null), FLASH_MS);
    return () => clearTimeout(timer);
  }, [flashId]);

  const empty = error ? (
    <LoadError lang={lang} onRetry={() => setReloadKey(k => k + 1)} />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
  );

  /* `orders` / `returns` from the detail path, as before; `payments` from the endpoint. */
  const idKey = (detailPath || endpoint)
    .split("/")
    .filter(Boolean)
    .pop();

  if (!loadedOnce) return <Skeleton active title={false} paragraph={{ rows: 4 }} className="cp-docstab" />;

  return (
    /* No gap: the footer brings its own top rule (`.lp-table__foot`), so it sits right under the
       table as it does on the full lists — a gap drew a second divider. */
    <Flex vertical className="cp-docstab">
      {renderSummary && count > 0 && !error && summary ? renderSummary(summary) : null}
      <Table
        className="lp-table__grid lp-fit"
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={false}
        scroll={{ x: "max-content" }}
        rowClassName={row => [clickableRows ? "cp-rowlink" : "", String(row.id) === flashId ? "cp-row--flash" : ""].filter(Boolean).join(" ")}
        onRow={clickableRows ? row => ({ onClick: () => onRowClick(row) }) : undefined}
        locale={{ emptyText: empty }}
      />
      {count > 0 ? (
        <TableFoot
          id={`cp-docs-${idKey}`}
          page={page}
          size={size}
          total={count}
          sizes={PAGE_SIZES}
          labels={{ perPage: t("rows_per_page"), range: t("range_of") }}
          onPage={setPage}
          onSize={next => {
            setSize(next);
            setPage(1);
          }}
        />
      ) : null}
    </Flex>
  );
};

export default DocsTab;
